if (!customElements.get('collection-infinite-scroll')) {
  customElements.define(
    'collection-infinite-scroll',
    class CollectionInfiniteScroll extends HTMLElement {
      connectedCallback() {
        this.grid = this.closest('.collection')?.querySelector('#product-grid');
        this.pagination = this.closest('.collection')?.querySelector('.pagination-wrapper');
        this.button = this.querySelector('button');
        this.status = this.querySelector('[role="status"]');
        this.nextUrl = this.dataset.nextUrl;

        if (!this.grid || !this.button || !this.nextUrl) return;

        this.abortController = new AbortController();
        this.onButtonClick = () => this.loadNextPage();
        this.button.addEventListener('click', this.onButtonClick);
        this.pagination?.classList.add('collection-infinite-scroll__pagination-hidden');

        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              if (entries.some((entry) => entry.isIntersecting)) this.loadNextPage();
            },
            { rootMargin: '400px 0px' },
          );
          this.observer.observe(this);
        }
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.abortController?.abort();
        this.button?.removeEventListener('click', this.onButtonClick);
      }

      async loadNextPage() {
        if (this.loading || !this.nextUrl || !this.isConnected) return;

        this.loading = true;
        this.button.disabled = true;
        this.button.textContent = this.dataset.loadingLabel;
        this.grid.setAttribute('aria-busy', 'true');

        try {
          const url = new URL(this.nextUrl, window.location.origin);
          if (url.origin !== window.location.origin) throw new Error('Invalid collection URL');
          url.searchParams.set('section_id', this.dataset.sectionId);

          const response = await fetch(url, { signal: this.abortController.signal });
          if (!response.ok) throw new Error(`Collection request failed: ${response.status}`);

          const page = new DOMParser().parseFromString(await response.text(), 'text/html');
          const sourceGrid = page.querySelector('#ProductGridContainer #product-grid');
          const sourcePagination = page.querySelector('#ProductGridContainer .pagination-wrapper');
          const products = sourceGrid ? Array.from(sourceGrid.children) : [];
          if (
            !products.length ||
            !products.every((product) => product.classList.contains('grid__item')) ||
            !sourcePagination ||
            Number(sourcePagination.dataset.page) <= Number(this.pagination?.dataset.page)
          ) {
            throw new Error('Collection response has no products');
          }

          if (!this.isConnected || this.abortController.signal.aborted) return;

          products.forEach((product) => {
            product.classList.add('scroll-trigger--cancel');
            product.querySelectorAll('.scroll-trigger').forEach((element) => {
              element.classList.add('scroll-trigger--cancel');
            });
          });
          this.grid.append(...products);

          if (this.pagination) {
            sourcePagination.classList.add('collection-infinite-scroll__pagination-hidden');
            this.pagination.replaceWith(sourcePagination);
            this.pagination = sourcePagination;
          }

          this.nextUrl = page.querySelector('#ProductGridContainer collection-infinite-scroll')?.dataset.nextUrl || '';
          this.status.textContent = this.nextUrl ? this.dataset.loadedLabel : this.dataset.completeLabel;

          if (this.nextUrl) {
            this.button.textContent = this.dataset.loadLabel;
            this.observer?.observe(this);
          } else {
            this.button.hidden = true;
            this.status.classList.remove('visually-hidden');
            this.observer?.disconnect();
          }
        } catch (error) {
          if (this.abortController.signal.aborted) return;
          console.error(error);
          this.observer?.disconnect();
          this.pagination?.classList.remove('collection-infinite-scroll__pagination-hidden');
          this.button.textContent = this.dataset.retryLabel;
          this.status.textContent = this.dataset.retryLabel;
        } finally {
          if (this.isConnected) {
            this.loading = false;
            this.button.disabled = false;
            this.grid.removeAttribute('aria-busy');
          }
        }
      }
    },
  );
}
